import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InlineError,
  Input,
  RequiredMark,
  useToast,
} from '@asad/crane-ui'

import { useAppDispatch } from '@/hooks/reduxHooks'
import { doLogin, doResetPassword, doSendEmailMfaCode } from '@/store/slices/auth-slice'

import {
  makeLoginSchema,
  makeResetPasswordSchema,
  type LoginFormValues,
  type ResetPasswordFormValues,
} from '../schemas/auth.schema'

/** Widok logowania (/auth/login) — prezentacja z
 *  `app/prototypes/src/features/auth/components/Login.tsx`.
 *  Zachowanie produkcyjne bez zmian: `doLogin` → rozgałęzienie na
 *  /auth/mfa-setup (`mfaSetupRequired`) albo /auth/mfa-verify (`mfaRequired`;
 *  dla metody EMAIL najpierw wysyłka kodu), błędy logowania jako powiadomienie,
 *  modal „Nie pamiętasz hasła?" z `doResetPassword` (odpowiedź nigdy nie ujawnia,
 *  czy konto istnieje).
 *
 *  Modal resetu hasła jest częścią tego pliku, bo w mockupie również jest
 *  częścią `Login.tsx` — nie ma dla niego osobnego komponentu źródłowego. */

const LOGIN_DEFAULTS: LoginFormValues = { email: '', password: '' }

export const Login = () => {
  const [showPassword, setShowPassword] = React.useState(false)
  const [forgotOpen, setForgotOpen] = React.useState(false)
  const [forgotSending, setForgotSending] = React.useState(false)
  const [forgotSent, setForgotSent] = React.useState(false)
  const [forgotErrorKey, setForgotErrorKey] = React.useState<string | null>(null)
  const forgotRequestGeneration = React.useRef(0)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const toast = useToast()

  const loginSchema = React.useMemo(() => makeLoginSchema(t), [t])
  const resetSchema = React.useMemo(() => makeResetPasswordSchema(t), [t])

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: LOGIN_DEFAULTS,
  })
  const forgotForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  })

  const isSubmitting = form.formState.isSubmitting

  React.useEffect(() => {
    if (sessionStorage.getItem('autoLoggedOut')) {
      sessionStorage.removeItem('autoLoggedOut')
      toast.warning(t('loginPage.loggedOut'))
    }
    // Powód przekierowania z /auth/mfa-verify po wyczerpaniu prób kodu MFA.
    if (sessionStorage.getItem('mfaAttemptsExhausted')) {
      sessionStorage.removeItem('mfaAttemptsExhausted')
      toast.warning(t('mfaVerify.exhaustedLoginError'))
    }
  }, [t, toast])

  /** Backend zwraca komunikat tekstowy albo klucz i18n — najpierw próbujemy klucza. */
  const translateLoginError = (message?: string) => {
    if (!message) return t('loginPage.genericLoginError')

    const translatedMessage = t(message)
    if (translatedMessage && translatedMessage !== message) return translatedMessage

    const lowered = message.toLowerCase()
    if (lowered.includes('block')) return t('loginPage.userBlocked')
    if (lowered.includes('credential') || lowered.includes('password') || lowered.includes('unauthorized')) {
      return t('loginPage.incorrectCredentials')
    }

    return t('loginPage.genericLoginError')
  }

  const onSubmit = async (values: LoginFormValues) => {
    try {
      /* Produkcyjny schemat przycinał oba pola (`z.string().trim()`) — zachowujemy to,
         żeby wklejone spacje nadal nie blokowały logowania. */
      const payload = await dispatch(doLogin({ email: values.email.trim(), password: values.password.trim() })).unwrap()

      if ('mfaSetupRequired' in payload) {
        navigate('/auth/mfa-setup', { replace: true })
        return
      }

      if ('mfaRequired' in payload) {
        if (payload.method === 'EMAIL') {
          await dispatch(doSendEmailMfaCode(i18n.language)).unwrap()
          toast.success(t('mfaVerify.emailSent'))

          navigate('/auth/mfa-verify', { replace: true, state: { emailJustSent: true } })
          return
        }
        navigate('/auth/mfa-verify', { replace: true })
      }
    } catch (error) {
      toast.error(translateLoginError(error as string))
    }
  }

  const closeForgot = () => {
    setForgotOpen(false)
    setForgotSent(false)
    setForgotSending(false)
    setForgotErrorKey(null)
    forgotRequestGeneration.current += 1
    forgotForm.reset({ email: '' })
  }

  /** Przyjęte żądanie zawsze kończy się tym samym komunikatem — nie ujawniamy, czy konto istnieje.
   *  Odrzucone (limit prób, brak połączenia) zostawia formularz do ponowienia; oba przypadki
   *  nie zależą od konta, bo backend liczy limit po adresie, zanim sprawdzi, czy konto istnieje. */
  const onForgotSubmit = async (values: ResetPasswordFormValues) => {
    const generation = ++forgotRequestGeneration.current
    setForgotSending(true)
    setForgotErrorKey(null)
    try {
      await dispatch(doResetPassword({ email: values.email.trim(), language: i18n.language })).unwrap()
      if (generation !== forgotRequestGeneration.current) return
      setForgotSent(true)
    } catch (error) {
      if (generation !== forgotRequestGeneration.current) return
      // Globalny middleware pomija tę akcję (INLINE_HANDLED_ACTIONS). Inline, nie toast — klik w toast
      // poza dialogiem zamyka go i kasuje wpisany adres.
      setForgotErrorKey(error as string)
    } finally {
      if (generation === forgotRequestGeneration.current) setForgotSending(false)
    }
  }

  return (
    <>
      <section className='w-full max-w-crane-card rounded-crane-md bg-crane-card p-6 shadow-crane-soft'>
        <h2 className='text-crane-2xl font-semibold leading-5 text-crane-foreground'>{t('loginPage.title')}</h2>
        <p className='mb-4 mt-0.5 text-xs text-crane-muted-foreground'>{t('loginPage.subtitle')}</p>

        <Form {...form}>
          <form className='space-y-6' onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <div className='space-y-4'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('common.email')}
                      <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        autoComplete='email'
                        placeholder={t('loginPage.emailPlaceholder')}
                        {...field}
                        onChange={(event) => field.onChange(event.target.value.toLowerCase())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('loginPage.password')}
                      <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <div className='relative'>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete='current-password'
                          className='pr-9'
                          {...field}
                        />
                        <button
                          type='button'
                          onClick={() => setShowPassword((value) => !value)}
                          aria-label={showPassword ? t('loginPage.hidePassword') : t('loginPage.showPassword')}
                          className='absolute inset-y-0 right-0 flex w-9 items-center justify-center text-crane-muted-foreground transition-colors hover:text-crane-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring'
                        >
                          {showPassword ? (
                            <EyeOff className='size-4' aria-hidden />
                          ) : (
                            <Eye className='size-4' aria-hidden />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='flex justify-end'>
                <button
                  type='button'
                  onClick={() => setForgotOpen(true)}
                  className='text-xs font-semibold text-crane-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring'
                >
                  {t('loginPage.forgotPassword')}
                </button>
              </div>
            </div>

            <Button type='submit' className='w-full' loading={isSubmitting} loadingLabel={t('loginPage.loggingIn')}>
              {t('loginPage.login')}
            </Button>
          </form>
        </Form>
      </section>

      <Dialog open={forgotOpen} onOpenChange={(open) => (open ? setForgotOpen(true) : closeForgot())}>
        <DialogContent className='max-w-crane-panel'>
          <DialogHeader>
            <DialogTitle className='text-crane-2xl'>{t('resetPassword.title')}</DialogTitle>
            <DialogDescription className='text-xs'>
              {forgotSent ? t('resetPassword.sent') : t('resetPassword.desc')}
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <DialogFooter>
              <Button type='button' onClick={closeForgot}>
                {t('commonActions.close')}
              </Button>
            </DialogFooter>
          ) : (
            <Form {...forgotForm}>
              <form className='space-y-4' onSubmit={forgotForm.handleSubmit(onForgotSubmit)} noValidate>
                <FormField
                  control={forgotForm.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('common.email')}
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <Input
                          type='email'
                          autoFocus
                          placeholder={t('loginPage.emailPlaceholder')}
                          {...field}
                          onChange={(event) => field.onChange(event.target.value.toLowerCase())}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {forgotErrorKey ? <InlineError>{t(forgotErrorKey)}</InlineError> : null}
                <DialogFooter>
                  <Button type='button' variant='ghost' onClick={closeForgot}>
                    {t('commonActions.cancel')}
                  </Button>
                  <Button type='submit' loading={forgotSending}>
                    {t('resetPassword.submit')}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Login
